import React, { useState } from "react";
import useApi from "../../../CustomHooks/useApi";
import { useSelector } from "react-redux";
import { ArrowLeft, Phone, FileText, IndianRupee, Lock, CheckCircle, Receipt, User, Calendar, Hash, AlertCircle, ChevronLeft } from "lucide-react";
import bharatConnectLogo from "../../../Assets/Images/bharat-connect-logo.svg";
import { useNavigate } from "react-router-dom";

const FetchBill = ({ billerInfo, onBack, selectedCategory }) => {
    console.log("FetchBill billerInfo:", billerInfo);

    const [customerMobile, setCustomerMobile] = useState("");
    const [dynamicInputs, setDynamicInputs]   = useState({});
    const [billData, setBillData]             = useState(null);
    const [loading, setLoading]               = useState(false);
    const [errorMessage, setErrorMessage]     = useState("");
    const [canPay, setCanPay]                 = useState(false);
    const [popupData, setPopupData]           = useState(null);

    // ── Pay modal state ──
    const [showPayModal, setShowPayModal]           = useState(false);
    const [amount, setAmount]                       = useState("");
    const [mpin, setMpin]                           = useState("");
    const [customerPan, setCustomerPan]             = useState("");
    const [customerName, setCustomerName]           = useState("");
    const [selectedPaymentMode, setSelectedPaymentMode] = useState("");

    // ── CC fee state (lives inside modal flow only) ──
    // modalStage: 'input' → user enters amount + MPIN and clicks Proceed
    //             'confirm' → fee shown, user clicks Pay Now
    const [modalStage,   setModalStage]   = useState("input");
    const [feeAmount,    setFeeAmount]    = useState(null);
    const [totalPayable, setTotalPayable] = useState(null);
    const [feeLoading,   setFeeLoading]   = useState(false);
    const [feeError,     setFeeError]     = useState(null);

    const navigate = useNavigate();
    const mid              = useSelector((s) => s.auth.employeeDetails?.username);
    const merchantAccountNo = useSelector((s) => s.auth.employeeDetails?.merchant_account_no);

    // ── API hooks ──
    const { callApi: fetchBillApi    } = useApi("/bbps/fetch-bill",    "POST");
    const { callApi: validateBillApi } = useApi("/bbps/validate-bill", "POST");
    const { callApi: payBillApi      } = useApi("/bbps/pay-bill",      "POST");
    const { callApi: feeCalcApi      } = useApi("/dmt/fee/calculate",  "POST");

    // ── Credit Card guard ──
    const isCreditCard =
        selectedCategory === "Credit Card" ||
        billerInfo?.biller?.billerCategory === "Credit Card";

    // ── Payment modes from biller info ──
    // API may return flat { billerPaymentModes } or nested { biller: { billerPaymentModes } } or array
    const _rawPaymentModes =
        billerInfo?.billerPaymentModes?.paymentModeInfo ||
        billerInfo?.biller?.billerPaymentModes?.paymentModeInfo ||
        (Array.isArray(billerInfo) ? billerInfo[0]?.billerPaymentModes?.paymentModeInfo : null);
    const paymentModeInfo = _rawPaymentModes
        ? (Array.isArray(_rawPaymentModes) ? _rawPaymentModes : [_rawPaymentModes])
        : [];

    // ── Input params ──
    const inputParams =
        billerInfo?.biller?.billerInputParams?.paramInfo
            ? Array.isArray(billerInfo.biller.billerInputParams.paramInfo)
                ? billerInfo.biller.billerInputParams.paramInfo
                : [billerInfo.biller.billerInputParams.paramInfo]
            : [];

    const handleInputChange = (paramName, value) =>
        setDynamicInputs((prev) => ({ ...prev, [paramName]: value }));

    const buildInputParams = () =>
        inputParams.map((item) => ({
            paramName:  item.paramName,
            paramValue: dynamicInputs[item.paramName] || "",
        }));

    // ── Use validate-bill when fetch is not supported or validation is required ──
    const shouldUseValidate =
        billerInfo?.biller?.billerFetchRequiremet === "NOT_SUPPORTED" ||
        billerInfo?.biller?.billerSupportBillValidation === "OPTIONAL" ||
        billerInfo?.biller?.billerSupportBillValidation === "MANDATORY";

    // ── Fetch / Validate Bill ──
    const handleFetchBill = async () => {
        try {
            setLoading(true);
            setErrorMessage("");
            setCanPay(false);
            setBillData(null);

            const params = buildInputParams();
            let response;

            if (shouldUseValidate) {
                response = await validateBillApi({
                    billerId: billerInfo.biller.billerId,
                    customerMobile,
                    inputParams: params,
                });
            } else {
                response = await fetchBillApi({
                    mid,
                    merchantAccountNo,
                    billerId: billerInfo.biller.billerId,
                    customerMobile,
                    inputParams: params,
                });
            }

            const isValidResponse =
                response?.success === true &&
                response?.message === "Success" &&
                response?.errorCode === null &&
                response?.data?.responseCode === "000";

            if (isValidResponse) {
                setBillData(response.data);
                setCanPay(true);
                // ── No fee calculation here for CC — happens inside modal ──
            } else {
                setBillData(null);
                setCanPay(false);
                setErrorMessage(
                    response?.data?.complianceReason ||
                    response?.data?.errorInfo?.error?.errorMessage ||
                    "Bill validation failed"
                );
            }
        } catch (error) {
            console.error(error);
            setBillData(null);
            setCanPay(false);
            setErrorMessage("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    // ── Reset entire flow and go back to biller selection ──
    const resetAndGoBack = () => {
        setPopupData(null);
        setShowPayModal(false);
        setModalStage("input");
        setFeeAmount(null);
        setTotalPayable(null);
        setFeeError(null);
        setBillData(null);
        setCanPay(false);
        setErrorMessage("");
        setCustomerMobile("");
        setDynamicInputs({});
        setAmount("");
        setMpin("");
        setCustomerPan("");
        setCustomerName("");
        onBack();
    };

    // ── Open pay modal — reset all modal state ──
    const openPayModal = () => {
        setAmount(getBestAmount());
        setMpin("");
        setModalStage("input");
        setFeeAmount(null);
        setTotalPayable(null);
        setFeeError(null);
        setCustomerPan("");
        setCustomerName("");
        setSelectedPaymentMode(paymentModeInfo[0]?.paymentMode || "Cash");
        setShowPayModal(true);
    };

    const closePayModal = () => {
        setShowPayModal(false);
        setModalStage("input");
        setFeeAmount(null);
        setTotalPayable(null);
        setFeeError(null);
        setSelectedPaymentMode("");
    };

    // ── CC: Calculate fee inside modal (Stage input → confirm) ──
    const handleModalProceed = async () => {
        if (!amount || Number(amount) <= 0) {
            setFeeError("Please enter a valid amount.");
            return;
        }
        if (!mpin) {
            setFeeError("Please enter your MPIN.");
            return;
        }

        setFeeLoading(true);
        setFeeError(null);

        try {
            const res = await feeCalcApi({
                provider:          "CCBP",
                sub_provider:      "BILL_AVENUE",
                amount:            parseFloat(amount),
                commercial_type:   "TRANSACTION_FEE",
                mid,
            });

            const fee   = res?.fee ?? res?.feeAmount ?? res?.calculatedFee ?? res?.transactionFee ?? 0;
            const total = res?.totalAmount ?? res?.totalPayable ?? (parseFloat(amount) + parseFloat(fee));

            setFeeAmount(parseFloat(fee));
            setTotalPayable(parseFloat(total));
            setModalStage("confirm");
        } catch (err) {
            setFeeError(
                err?.response?.data?.message ||
                "Could not calculate transaction fee. Please try again."
            );
        } finally {
            setFeeLoading(false);
        }
    };

    const isHighValue = Number(amount) >= 50000;

    // ── Pay Bill ──
    const handlePayBill = async () => {
        if (isHighValue) {
            if (!customerPan.trim()) {
                setFeeError("PAN is required for payments of ₹50,000 or above.");
                return;
            }
            if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(customerPan.trim().toUpperCase())) {
                setFeeError("Enter a valid 10-character PAN (e.g. ABCDE1234F).");
                return;
            }
            if (!customerName.trim()) {
                setFeeError("Customer name is required for payments of ₹50,000 or above.");
                return;
            }
        }

        try {
            setLoading(true);

            const payload = {
                mid,
                mpin,
                merchantAccountNo,
                billerId:    billerInfo?.biller?.billerId,
                customerMobile,
                billerAdhoc: billerInfo?.biller?.billerMode === "ADHOC",
                amount:      Number(amount),
                // CC only: pass total (bill + fee) in txnAmount
                ...(isCreditCard && totalPayable ? { txnAmount: totalPayable } : {}),
                inputParams:  billData?.inputParams?.input || buildInputParams(),
                requestId:    billData?.requestId,
                billerResponse: {
                    billAmount:   billData?.billerResponse?.billAmount,
                    billDate:     billData?.billerResponse?.billDate,
                    billNumber:   billData?.billerResponse?.billNumber,
                    billPeriod:   billData?.billerResponse?.billPeriod,
                    customerName: billData?.billerResponse?.customerName,
                    dueDate:      billData?.billerResponse?.dueDate,
                    ...(isHighValue ? {
                        customerPan:  customerPan.trim().toUpperCase(),
                        customerName: customerName.trim(),
                    } : {}),
                },
                additionalInfo: { info: billData?.additionalInfo?.info },
                paymentMode: selectedPaymentMode,
                quickPay:    false,
            };

            const response = await payBillApi(payload);

            closePayModal();

            if (response?.success && response?.data?.status === "SUCCESS") {
                setPopupData({ type: "success", title: "Payment Successful", data: response.data });
            } else {
                setPopupData({
                    type:    "error",
                    title:   "Payment Failed",
                    message: response?.message || response?.data?.customerSummary || "Payment Failed",
                });
            }
        } catch (error) {
            console.error(error);
            closePayModal();
            setPopupData({
                type:    "error",
                title:   "Payment Failed",
                message: error?.response?.data?.message || error?.message || "Something went wrong",
            });
        } finally {
            setLoading(false);
        }
    };

    // ── Helpers ──
    const billerName = billerInfo?.biller?.billerName || billerInfo?.billerName || "Bill Payment";
    const billAmount = billData?.billerResponse?.billAmount;

    const getBestAmount = () => {
        if (billAmount && parseFloat(billAmount) > 0) return billAmount;
        const infoList = Array.isArray(billData?.additionalInfo?.info)
            ? billData.additionalInfo.info
            : billData?.additionalInfo?.info ? [billData.additionalInfo.info] : [];
        const outstanding = infoList.find(
            (i) => i.infoName?.toLowerCase().includes("outstanding") || i.infoName?.toLowerCase().includes("total")
        );
        if (outstanding && parseFloat(outstanding.infoValue) > 0) return outstanding.infoValue;
        const firstPositive = infoList.find((i) => parseFloat(i.infoValue) > 0);
        return firstPositive ? firstPositive.infoValue : billAmount || "";
    };

    const BillDetailRow = ({ icon: Icon, label, value }) => {
        if (!value || value === "NA") return null;
        return (
            <div className="flex items-start justify-between py-2.5 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-2 text-slate-400">
                    <Icon size={13} />
                    <span className="text-xs">{label}</span>
                </div>
                <span className="text-xs font-semibold text-slate-800 text-right max-w-[55%]">{value}</span>
            </div>
        );
    };

    const additionalInfoList = Array.isArray(billData?.additionalInfo?.info)
        ? billData.additionalInfo.info
        : billData?.additionalInfo?.info ? [billData.additionalInfo.info] : [];

    /* ══════════════════════════════════════════════════════════ */

    return (
        <div className="min-h-screen bg-slate-50">

            {/* Header */}
            <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium">
                        <ArrowLeft size={16} /> Back
                    </button>
                    <div className="h-5 w-px bg-slate-200" />
                    <div>
                        <h1 className="text-base font-bold text-slate-800">{billerName}</h1>
                        <p className="text-xs text-slate-400">
                            {isCreditCard
                                ? "Credit Card Bill Payment · Fee calculated at checkout"
                                : "Enter customer details to fetch bill"}
                        </p>
                    </div>
                </div>
                <img src={bharatConnectLogo} alt="Bharat Connect" className="h-7" />
            </div>

            {/* Two-panel layout */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">

                {/* LEFT — Input Form */}
                <div className="bg-white rounded-2xl shadow-sm border p-6">
                    <h2 className="text-sm font-semibold text-slate-700 mb-5">Customer Details</h2>
                    <div className="space-y-4">

                        {/* Customer Mobile */}
                        <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">Customer Mobile</label>
                            <div className="relative">
                                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Enter mobile number"
                                    value={customerMobile}
                                    onChange={(e) => setCustomerMobile(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 bg-slate-50"
                                />
                            </div>
                        </div>

                        {/* Dynamic Fields */}
                        {inputParams.map((field, index) => (
                            <div key={index}>
                                <label className="text-xs font-medium text-slate-500 mb-1 block">{field.paramName}</label>
                                <div className="relative">
                                    <FileText size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder={`Enter ${field.paramName}`}
                                        value={dynamicInputs[field.paramName] || ""}
                                        onChange={(e) => handleInputChange(field.paramName, e.target.value)}
                                        className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 bg-slate-50"
                                    />
                                </div>
                                {field.minLength && field.maxLength && (
                                    <p className="text-xs text-slate-400 mt-1 ml-1">{field.minLength}–{field.maxLength} characters</p>
                                )}
                            </div>
                        ))}

                        {/* CC notice */}
                        {isCreditCard && (
                            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                                <AlertCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-700">
                                    Transaction fee will be calculated at the time of payment based on the entered amount.
                                </p>
                            </div>
                        )}

                        {/* Fetch Bill Button */}
                        <button
                            onClick={handleFetchBill}
                            disabled={loading}
                            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors text-sm mt-2"
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <Receipt size={16} />
                                    {shouldUseValidate ? "Validate Bill" : "Fetch Bill"}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* RIGHT — Bill Details */}
                <div className="bg-white rounded-2xl shadow-sm border p-6">
                    {!billData ? (
                        errorMessage ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="w-full bg-red-50 border border-red-200 rounded-xl p-4">
                                    <p className="text-sm font-medium text-red-600">{errorMessage}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-3 py-10">
                                <Receipt size={48} strokeWidth={1} />
                                <p className="text-sm text-slate-400">Fetched bill will appear here</p>
                            </div>
                        )
                    ) : (
                        <div>
                            <div className="flex items-center gap-2 mb-5">
                                <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center">
                                    <CheckCircle size={15} className="text-green-600" />
                                </div>
                                <h2 className="text-sm font-semibold text-slate-700">Bill Details</h2>
                            </div>

                            <BillDetailRow icon={User}     label="Customer Name" value={billData?.billerResponse?.customerName} />
                            {(
                                Array.isArray(billData?.inputParams?.input)
                                    ? billData.inputParams.input
                                    : billData?.inputParams?.input ? [billData.inputParams.input] : []
                            ).map((item, index) => (
                                <BillDetailRow key={index} icon={Hash}     label={item?.paramName}  value={item?.paramValue} />
                            ))}
                            <BillDetailRow icon={Calendar} label="Bill Date"   value={billData?.billerResponse?.billDate} />
                            <BillDetailRow icon={Calendar} label="Due Date"    value={billData?.billerResponse?.dueDate} />
                            <BillDetailRow icon={FileText} label="Bill Number" value={billData?.billerResponse?.billNumber} />
                            <BillDetailRow icon={FileText} label="Bill Period" value={billData?.billerResponse?.billPeriod} />

                            {additionalInfoList.length > 0 && (
                                <div className="mt-3 rounded-xl border border-slate-100 overflow-hidden">
                                    {additionalInfoList.map((info, idx) => (
                                        <div key={idx} className={`flex items-center justify-between px-4 py-2.5 ${idx !== additionalInfoList.length - 1 ? "border-b border-slate-100" : ""} ${idx % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
                                            <span className="text-xs text-slate-500">{info.infoName}</span>
                                            <span className="text-xs font-bold text-slate-800">₹{info.infoValue}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {billData?.billerResponse?.billAmount && (
                                <div className="flex items-center justify-between mt-4 bg-blue-50 rounded-xl px-4 py-3">
                                    <span className="text-sm font-semibold text-blue-700">Bill Amount</span>
                                    <span className="text-lg font-bold text-blue-700">₹{billData.billerResponse.billAmount}</span>
                                </div>
                            )}

                            {canPay && (
                                <button
                                    onClick={openPayModal}
                                    className="w-full mt-4 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
                                >
                                    <IndianRupee size={16} />
                                    Pay Now
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════
                PAY MODAL
                Non-CC  → single stage: amount + MPIN + Pay Now
                CC      → stage 'input':   amount (editable) + MPIN + Proceed
                        → stage 'confirm': fee breakdown + Pay Now
            ══════════════════════════════════════════════════════ */}
            {showPayModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

                        {/* Modal header */}
                        <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
                            <div>
                                <h3 className="text-white font-bold text-base">
                                    {isCreditCard && modalStage === "confirm" ? "Fee Breakdown" : "Confirm Payment"}
                                </h3>
                                <p className="text-blue-100 text-xs mt-0.5">{billerName}</p>
                            </div>
                            {/* Back to input (CC confirm stage only) */}
                            {isCreditCard && modalStage === "confirm" && (
                                <button
                                    onClick={() => {
                                        setModalStage("input");
                                        setFeeAmount(null);
                                        setTotalPayable(null);
                                        setFeeError(null);
                                    }}
                                    className="flex items-center gap-1 text-blue-100 hover:text-white text-xs font-medium"
                                >
                                    <ChevronLeft size={14} /> Edit
                                </button>
                            )}
                        </div>

                        {/* ── STAGE: input (both CC and non-CC) ── */}
                        {(!isCreditCard || modalStage === "input") && (
                            <div className="px-6 py-5 space-y-4">

                                {/* Amount */}
                                <div>
                                    <label className="text-xs font-medium text-slate-500 mb-1 block">Amount</label>
                                    <div className="relative">
                                        <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="number"
                                            placeholder="Enter amount"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 bg-slate-50"
                                        />
                                    </div>
                                    {isCreditCard && (
                                        <p className="text-xs text-slate-400 mt-1 ml-1">
                                            You can edit the amount. Fee will be calculated on Proceed.
                                        </p>
                                    )}
                                </div>

                                {/* Payment Mode Selector */}
                                {paymentModeInfo.length > 0 && (
                                    <div>
                                        <label className="text-xs font-medium text-slate-500 mb-2 block">Payment Mode</label>
                                        <div className="flex flex-wrap gap-2">
                                            {paymentModeInfo.map((mode) => (
                                                <button
                                                    key={mode.paymentMode}
                                                    type="button"
                                                    onClick={() => setSelectedPaymentMode(mode.paymentMode)}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                                                        selectedPaymentMode === mode.paymentMode
                                                            ? "bg-blue-600 text-white border-blue-600"
                                                            : "bg-white text-slate-600 border-slate-200 hover:border-blue-400"
                                                    }`}
                                                >
                                                    {mode.paymentMode}
                                                </button>
                                            ))}
                                        </div>
                                        {paymentModeInfo
                                            .filter(x => x.paymentMode === selectedPaymentMode)
                                            .slice(0, 1)
                                            .map(m => (
                                                <p key={m.paymentMode} className="text-xs text-slate-400 mt-1 ml-1">
                                                    Limit: Rs.{parseInt(m.minAmount).toLocaleString()} - Rs.{parseInt(m.maxAmount).toLocaleString()}
                                                </p>
                                            ))
                                        }
                                    </div>
                                )}

                                {/* High-value fields (≥ ₹50,000) */}
                                {isHighValue && (
                                    <>
                                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                                            <AlertCircle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                                            <p className="text-xs text-amber-700 font-medium">
                                                PAN and customer name are mandatory for payments of ₹50,000 or above.
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-slate-500 mb-1 block">
                                                Customer PAN <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <FileText size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                                <input
                                                    type="text"
                                                    placeholder="e.g. ABCDE1234F"
                                                    value={customerPan}
                                                    maxLength={10}
                                                    onChange={(e) => setCustomerPan(e.target.value.toUpperCase())}
                                                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 bg-slate-50 tracking-widest font-mono"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-slate-500 mb-1 block">
                                                Customer Name <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                                <input
                                                    type="text"
                                                    placeholder="Full name as per PAN"
                                                    value={customerName}
                                                    onChange={(e) => setCustomerName(e.target.value)}
                                                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 bg-slate-50"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* MPIN */}
                                <div>
                                    <label className="text-xs font-medium text-slate-500 mb-1 block">Enter MPIN</label>
                                    <div className="relative">
                                        <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="password"
                                            placeholder="••••••"
                                            value={mpin}
                                            maxLength={6}
                                            onChange={(e) => setMpin(e.target.value)}
                                            className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 bg-slate-50 tracking-widest"
                                        />
                                    </div>
                                </div>

                                {/* Validation / fee error */}
                                {feeError && (
                                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                                        <AlertCircle size={13} className="text-red-500 shrink-0 mt-0.5" />
                                        <p className="text-xs text-red-600">{feeError}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── STAGE: confirm (CC only) — fee breakdown ── */}
                        {isCreditCard && modalStage === "confirm" && (
                            <div className="px-6 py-5">
                                <div className="rounded-xl border border-slate-200 overflow-hidden text-sm mb-4">
                                    <div className="flex justify-between items-center px-4 py-3 bg-slate-50 border-b border-slate-100">
                                        <span className="text-slate-500 text-xs">Bill Amount</span>
                                        <span className="font-semibold text-slate-800">₹{parseFloat(amount).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center px-4 py-3 bg-slate-50 border-b border-slate-100">
                                        <span className="text-slate-500 text-xs">Transaction Fee</span>
                                        <span className="font-semibold text-orange-600">+ ₹{feeAmount?.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center px-4 py-4 bg-green-50">
                                        <span className="text-green-700 font-bold text-sm">Total Payable</span>
                                        <span className="text-green-700 font-bold text-lg">₹{totalPayable?.toFixed(2)}</span>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-400 text-center">
                                    ₹{totalPayable?.toFixed(2)} will be deducted from your wallet.
                                </p>
                            </div>
                        )}

                        {/* Modal actions */}
                        <div className="px-6 pb-5 flex gap-3">
                            <button
                                onClick={resetAndGoBack}
                                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                            >
                                Cancel
                            </button>

                            {/* Non-CC: direct Pay Now */}
                            {!isCreditCard && (
                                <button
                                    onClick={handlePayBill}
                                    disabled={loading}
                                    className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-60"
                                >
                                    {loading ? "Processing..." : "Pay Now"}
                                </button>
                            )}

                            {/* CC input stage: Proceed → fee calc */}
                            {isCreditCard && modalStage === "input" && (
                                <button
                                    onClick={handleModalProceed}
                                    disabled={feeLoading}
                                    className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
                                >
                                    {feeLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Calculating...
                                        </>
                                    ) : "Proceed"}
                                </button>
                            )}

                            {/* CC confirm stage: final Pay Now */}
                            {isCreditCard && modalStage === "confirm" && (
                                <button
                                    onClick={handlePayBill}
                                    disabled={loading}
                                    className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-60"
                                >
                                    {loading ? "Processing..." : "Pay Now"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Result Popup ── */}
            {popupData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
                        <h2 className={`text-xl font-bold mb-4 ${popupData.type === "success" ? "text-green-600" : "text-red-600"}`}>
                            {popupData.title}
                        </h2>
                        {popupData.type === "success" ? (
                            <div className="space-y-2 text-sm">
                                <p><span className="font-semibold">Transaction ID:</span> {popupData.data?.txnId}</p>
                                <p><span className="font-semibold">Amount:</span> ₹{popupData.data?.amount}</p>
                                <p><span className="font-semibold">Message:</span> {popupData.data?.customerSummary}</p>
                                <p><span className="font-semibold">Balance:</span> ₹{popupData.data?.balance?.balance?.toFixed(2)}</p>
                            </div>
                        ) : (
                            <div className="text-sm text-red-600">{popupData.message}</div>
                        )}
                        <button
                            className="w-full mt-6 bg-blue-600 text-white py-2 rounded-lg"
                            onClick={() => {
                                if (popupData.type === "success") {
                                    setPopupData(null);
                                    navigate("/transactionhistory");
                                } else {
                                    resetAndGoBack();
                                }
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FetchBill;
